/**
 * Authority Assigned Complaints View
 * Displays and manages assigned complaints
 */

import React, { useState } from 'react';

import TablePagination from '../../components/TablePagination';

const AssignedComplaintsView = ({
  complaints = [],
  searchTerm = '',
  filterStatus = 'all',
  onComplaintEdit,
  isLoading = false,
  error = null,
}) => {
  const errorMessage = typeof error === 'string'
    ? error
    : error?.message || null;

  // Get status color for badges
  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'Under Review': return 'bg-blue-100 text-blue-800';
      case 'Approved': return 'bg-green-100 text-green-800';
      case 'Rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Ensure complaints is always an array
  const complaintsArray = Array.isArray(complaints) ? complaints : [];

  const normalizeToString = (value) => {
    if (value === null || value === undefined) return '';
    return String(value);
  };

  // Filter complaints based on search term only
  const filteredComplaints = complaintsArray.filter((complaint) => {
    if (!searchTerm.trim()) return true;

    const term = searchTerm.trim().toLowerCase();
    const complaintId = normalizeToString(complaint.complaint_id).toLowerCase();
    const complaintType = normalizeToString(complaint.complaint_type).toLowerCase();
    const department = normalizeToString(complaint.department_name).toLowerCase();
    const priority = normalizeToString(complaint.priority).toLowerCase();
    const statusValue = normalizeToString(complaint.complaint_status || complaint.status).toLowerCase();
    const remark = normalizeToString(complaint.remark).toLowerCase();

    return complaintId.includes(term) ||
      complaintType.includes(term) ||
      department.includes(term) ||
      priority.includes(term) ||
      statusValue.includes(term) || remark.includes(term);
  });

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Pagination Logic
  const totalItems = filteredComplaints.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedComplaints = filteredComplaints.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );


  // Transform complaints data for DataGrid
  const transformedComplaints = filteredComplaints.map((complaint) => ({
    id: complaint.id || complaint.complaint_id || Math.random(),
    ...complaint,
  }));

  return (
    <div className="h-full flex flex-col min-h-0">
      {/* Complaints Table */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 flex-1 flex flex-col overflow-hidden min-h-0">
        <div className="flex-1 overflow-auto">
          <table className="min-w-full divide-y divide-gray-200 relative">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Complaint ID</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Complaint Type</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Complaint Status</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Assigned Complaint Type</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Department</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Assigned Days (CVO)</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Priority</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Remark</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr><td colSpan="9" className="px-6 py-12 text-center text-gray-500">Loading...</td></tr>
              ) : filteredComplaints.length === 0 ? (
                <tr><td colSpan="9" className="px-6 py-12 text-center text-gray-500">No complaints found</td></tr>
              ) : (
                paginatedComplaints.map((row) => (
                  <tr key={row.id || row.complaint_id || Math.random()} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.complaint_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.complaint_type}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const statusValue = normalizeToString(row.complaint_status || row.status);
                        const statusLower = statusValue.toLowerCase();
                        let styles = "bg-gray-100 text-gray-800 border-gray-200";

                        if (statusLower === 'pending') styles = "bg-yellow-50 text-yellow-800 border-yellow-200";
                        else if (['under review', 'under_review', 'in progress', 'in_progress'].includes(statusLower)) styles = "bg-blue-50 text-blue-700 border-blue-200";
                        else if (['approved', 'closed', 'resolved'].includes(statusLower)) styles = "bg-green-50 text-green-700 border-green-200";
                        else if (statusLower === 'rejected') styles = "bg-red-50 text-red-700 border-red-200";

                        return (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles}`}>
                            {statusValue || '—'}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{normalizeToString(row?.assign_complaint_type?.name || '') || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.department_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.assigned_day_by_cvo ?? '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const priority = normalizeToString(row.priority);
                        let styles = "bg-gray-100 text-gray-800 border-gray-200";

                        if (['High', 'P1'].includes(priority)) styles = "bg-red-50 text-red-700 border-red-200";
                        else if (['Medium', 'P2'].includes(priority)) styles = "bg-yellow-50 text-yellow-800 border-yellow-200";
                        else if (['Low', 'P3'].includes(priority)) styles = "bg-green-50 text-green-700 border-green-200";

                        return (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles}`}>
                            {priority || '—'}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate" title={normalizeToString(row.remark)}>
                      {normalizeToString(row.remark) || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => onComplaintEdit && onComplaintEdit(row)}
                        className="text-blue-600 hover:text-blue-900 transition-colors"
                        title="Edit Complaint"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(newSize) => {
            setItemsPerPage(newSize);
            setCurrentPage(1);
          }}
        />
      </div>
    </div>
  );
};

export default AssignedComplaintsView;

